import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { isNativeApp } from "@/lib/platform";
import { hasSeenWelcome } from "@/pages/Welcome";

interface Props {
  children: React.ReactNode;
  requireOnboarded?: boolean;
  /**
   * If true, an *already*-onboarded user landing on this route is bounced to
   * /app. Used by /onboarding so finished users don't see the calibration
   * screen on every visit (and don't trigger an in-component navigate() that
   * fights with this RequireAuth and produces a redirect ping-pong).
   */
  redirectIfOnboarded?: boolean;
}

// In-memory cache so navigations after onboarding don't re-flicker through a spinner.
const onboardedCache = new Map<string, boolean>();
export const setOnboardedCache = (userId: string, value: boolean) => {
  onboardedCache.set(userId, value);
};

export const RequireAuth = ({
  children,
  requireOnboarded = true,
  redirectIfOnboarded = false,
}: Props) => {
  const { session, loading } = useAuth();
  const userId = session?.user?.id;
  const cachedOnboarded = userId ? onboardedCache.get(userId) : undefined;
  const [onboarded, setOnboarded] = useState<boolean | null>(cachedOnboarded ?? null);
  const effectiveOnboarded = cachedOnboarded ?? onboarded;
  const location = useLocation();

  useEffect(() => {
    if (!userId) {
      setOnboarded(null);
      return;
    }
    const cached = onboardedCache.get(userId);
    if (cached !== undefined) {
      setOnboarded(cached);
      return;
    }
    let active = true;
    supabase
      .from("profiles")
      .select("onboarded")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        const value = !!data?.onboarded;
        onboardedCache.set(userId, value);
        setOnboarded(value);
      });
    return () => { active = false; };
  }, [userId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) return <Navigate to="/auth" replace state={{ from: location }} />;

  if (requireOnboarded || redirectIfOnboarded) {
    if (effectiveOnboarded === null) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }
    if (requireOnboarded && !effectiveOnboarded) {
      // Skip the marketing landing page after login — go straight to the
      // native onboarding intro / profile-setup wizard.
      return <Navigate to="/onboarding" replace />;
    }
    if (redirectIfOnboarded && effectiveOnboarded) {
      return <Navigate to="/app" replace />;
    }
  }

  return <>{children}</>;
};
